import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { MARQUEE_DEFAULT } from "@/components/site/Marquee";
import { buildFaq } from "@/components/site/Faq";
import { PRIVACY, TERMS } from "@/content/legal";

const field = "w-full rounded-xl border border-white/10 bg-ink px-4 py-3 text-paper outline-none transition-colors focus:border-volt";
const Box = ({ title, hint, children, testId }) => (
  <section data-testid={testId} className="rounded-3xl bg-surface p-6 ring-1 ring-white/[0.07] md:p-8">
    <h3 className="font-display text-xl text-paper">{title}</h3>
    {hint ? <p className="mt-1 text-sm text-zinc-400">{hint}</p> : null}
    <div className="mt-5 space-y-4">{children}</div>
  </section>
);
const SaveBtn = ({ onClick, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} className="rounded-full bg-volt px-6 py-2.5 font-semibold text-ink transition-transform hover:scale-[1.03]">Simpan</button>
);

const useSave = () => async (patch, label) => {
  try {
    await api.put("/admin/content", patch);
    toast.success(`${label} disimpan`);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
};

const FaqEditor = ({ initial, save }) => {
  const [items, setItems] = useState(initial);
  const set = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <Box testId="admin-faq-panel" title="Yang sering ditanya (FAQ)">
      {items.map((x, i) => (
        <div key={i} className="space-y-2 rounded-2xl border border-white/10 p-4" data-testid={`admin-faq-item-${i}`}>
          <div className="flex gap-2">
            <input data-testid={`admin-faq-q-${i}`} placeholder="Pertanyaan" value={x.q} onChange={(e) => set(i, "q", e.target.value)} className={field} />
            <button type="button" data-testid={`admin-faq-delete-${i}`} onClick={() => setItems(items.filter((_, j) => j !== i))} className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-white/15 hover:border-red-400" aria-label="Hapus"><Trash2 className="h-4 w-4" /></button>
          </div>
          <textarea data-testid={`admin-faq-a-${i}`} rows={3} placeholder="Jawaban" value={x.a} onChange={(e) => set(i, "a", e.target.value)} className={field} />
        </div>
      ))}
      <div className="flex flex-wrap gap-3">
        <button type="button" data-testid="admin-faq-add" onClick={() => setItems([...items, { q: "", a: "" }])} className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-zinc-200 hover:border-volt"><Plus className="h-4 w-4" /> Tambah pertanyaan</button>
        <SaveBtn testId="admin-faq-save" onClick={() => save({ faq: items.filter((x) => x.q.trim() && x.a.trim()) }, "FAQ")} />
      </div>
    </Box>
  );
};

const LegalEditor = ({ kind, title, initialBody, initialUpdated, save }) => {
  const [body, setBody] = useState(initialBody);
  const [updated, setUpdated] = useState(initialUpdated);
  return (
    <Box testId={`admin-${kind}-panel`} title={title} hint="Pisahkan paragraf dengan baris kosong. Awali baris dengan ## untuk sub-judul.">
      <input data-testid={`admin-${kind}-updated`} placeholder="Berlaku sejak (contoh: 1 Januari 2026)" value={updated} onChange={(e) => setUpdated(e.target.value)} className={field} />
      <textarea data-testid={`admin-${kind}-body`} rows={14} value={body} onChange={(e) => setBody(e.target.value)} className={`${field} font-mono text-sm`} />
      <SaveBtn testId={`admin-${kind}-save`} onClick={() => save({ [kind]: body, [`${kind}_updated`]: updated }, title)} />
    </Box>
  );
};

export const ContentAdmin = ({ rates }) => {
  const [c, setC] = useState(null);
  const [marquee, setMarquee] = useState("");
  const save = useSave();
  useEffect(() => {
    api.get("/admin/content").then((r) => {
      setC(r.data || {});
      setMarquee((r.data?.marquee?.length ? r.data.marquee : MARQUEE_DEFAULT).join("\n"));
    }).catch(() => setC({}));
  }, []);
  if (!c) return null;
  const faq = c.faq?.length ? c.faq : buildFaq(rates).map(([q, a]) => ({ q, a }));
  return (
    <div className="space-y-6" data-testid="admin-content-panel">
      <Box testId="admin-marquee-panel" title="Running text" hint="Satu baris = satu kalimat di running text.">
        <textarea data-testid="admin-marquee-input" rows={6} value={marquee} onChange={(e) => setMarquee(e.target.value)} className={field} />
        <SaveBtn testId="admin-marquee-save" onClick={() => save({ marquee: marquee.split("\n") }, "Running text")} />
      </Box>
      <FaqEditor initial={faq} save={save} />
      <LegalEditor kind="privacy" title="Privacy Policy" initialBody={c.privacy || PRIVACY.body} initialUpdated={c.privacy_updated || ""} save={save} />
      <LegalEditor kind="terms" title="Terms and Conditions" initialBody={c.terms || TERMS.body} initialUpdated={c.terms_updated || ""} save={save} />
    </div>
  );
};
